import React, { useMemo, useState } from "react";
import type { VendorImportAction, VendorImportPreview, VendorImportResult } from "./vendorMasterTypes";

interface Props {
  preview: VendorImportPreview;
  reason: string;
  applying: boolean;
  result?: VendorImportResult | null;
  onReasonChange: (reason: string) => void;
  onApply: () => void;
  onRemoveRow: (index: number) => void;
}

const labels: Record<VendorImportAction, string> = {
  CREATE: "신규",
  UPDATE: "자동수정",
  NO_CHANGE: "변경없음",
  CONFLICT: "충돌",
  ERROR: "오류",
};

const colors: Record<VendorImportAction, string> = {
  CREATE: "bg-emerald-100 text-emerald-700",
  UPDATE: "bg-blue-100 text-blue-700",
  NO_CHANGE: "bg-slate-200 text-slate-700",
  CONFLICT: "bg-amber-100 text-amber-800",
  ERROR: "bg-red-100 text-red-700",
};

const maskBusinessNumber = (value: unknown) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 10 ? `${digits.slice(0, 3)}-**-${digits.slice(-5)}` : "";
};

export default function VendorImportPanel({ preview, reason, applying, result, onReasonChange, onApply, onRemoveRow }: Props) {
  const [filter, setFilter] = useState<"ALL" | VendorImportAction>("ALL");
  const rows = useMemo(
    () => preview.rows.map((row, originalIndex) => ({ row, originalIndex })).filter(({ row }) => filter === "ALL" || row.status === filter),
    [filter, preview.rows]
  );
  const counts = [
    ["전체", preview.total],
    ["신규", preview.createCount],
    ["자동수정", preview.updateCount],
    ["변경없음", preview.noChangeCount],
    ["충돌", preview.conflictCount],
    ["오류", preview.errorCount],
  ];

  return (
    <section className="rounded-2xl border border-emerald-200 bg-white p-4" aria-label="거래처 Excel 미리보기">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h4 className="text-base font-black text-slate-900">Excel UPSERT 미리보기</h4>
          <p className="mt-1 text-xs font-bold leading-5 text-slate-500">빈 셀은 기존 값을 유지하며 CREATE·UPDATE만 적용됩니다.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {counts.map(([label, count]) => <span key={String(label)} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">{label} {count}건</span>)}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-[180px_minmax(0,1fr)_auto]">
        <select value={filter} onChange={(event) => setFilter(event.target.value as "ALL" | VendorImportAction)} className="min-h-[44px] rounded-xl border border-slate-200 px-3 text-sm font-bold">
          <option value="ALL">전체 처리결과</option>
          {(Object.keys(labels) as VendorImportAction[]).map((status) => <option key={status} value={status}>{labels[status]}</option>)}
        </select>
        <input value={reason} onChange={(event) => onReasonChange(event.target.value)} placeholder="Excel 적용 사유 (필수)" className="min-h-[44px] rounded-xl border border-slate-200 px-3 text-sm font-bold" />
        <button type="button" onClick={onApply} disabled={applying || preview.readyCount < 1 || !reason.trim()} className="min-h-[44px] rounded-xl bg-emerald-700 px-4 text-sm font-black text-white disabled:bg-slate-300">{applying ? "적용 중..." : `${preview.readyCount}건 일괄 반영`}</button>
      </div>
      {result && <div className={`mt-3 rounded-xl px-3 py-2 text-sm font-black ${result.status === "COMPLETED" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`}>{result.message || `${result.status}: 신규 ${result.createdCount || 0}건 / 수정 ${result.updatedCount || 0}건 / 실패 ${result.failedCount || 0}건`}</div>}
      <div className="mt-3 max-h-[420px] overflow-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[760px] text-left text-xs">
          <thead className="sticky top-0 bg-slate-100 text-slate-600"><tr>{["행", "업체코드", "상호명", "처리 예정", "기존 거래처", "변경 필드", "오류·충돌", ""].map((label) => <th key={label} className="px-3 py-2 font-black">{label}</th>)}</tr></thead>
          <tbody>
            {rows.map(({ row, originalIndex }) => {
              const vendor = row.vendor || {};
              return <tr key={`${row.rowNumber}-${originalIndex}`} className="border-t border-slate-100 align-top">
                <td className="px-3 py-2 font-bold">{row.rowNumber}</td>
                <td className="px-3 py-2 font-black">{String(vendor.vendorCode || row.existingVendor?.vendorCode || "-")}</td>
                <td className="px-3 py-2"><span className="font-black">{String(vendor.businessName || "-")}</span>{vendor.businessNumber && <span className="block text-slate-400">{maskBusinessNumber(vendor.businessNumber)}</span>}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-1 font-black ${colors[row.status]}`}>{labels[row.status]}</span></td>
                <td className="px-3 py-2">{row.existingVendor?.businessName || "-"}</td>
                <td className="px-3 py-2">{row.changedFields?.join(", ") || "-"}</td>
                <td className="max-w-[240px] px-3 py-2 font-bold text-red-700">{row.errors?.join(" / ") || "-"}</td>
                <td className="px-3 py-2"><button type="button" onClick={() => onRemoveRow(originalIndex)} disabled={applying} className="rounded-lg bg-slate-100 px-2 py-1 font-black text-slate-600">제외</button></td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
